import { DateTime } from "luxon";
import { fenceValueWithVat, otherValueWithVat } from "@/utils";
import { pdf_contract, CONTRACT_FORM_CODE } from "@/documents/contract/pdf_contract.js";
import { moduleSizeText, moduleHeightText, moduleNameKey, isPost } from "@/utils/fenceModules";

export default {
  create(contract, executantDetails, firmaMontajContract) {
    return {
      contract,
      executantDetails,
      firmaMontajContract,

      get language() {
        return 'hu-HU'
      },

      get isMontajSeparat() {
        return this.contract.detalii.montaj_separat
      },

      get valMasuratori() {
        if (Number(this.contract.masuratori_incluse) === 2) {
          return this.contract.val_masuratori ? Number(this.contract.val_masuratori) * 1.21 : 0
        } else {
          return 0
        }
      },

      get valMontaj() {
        if (Number(this.contract.montaj_inclus) === 2) {
          return this.contract.val_montaj ? Number(this.contract.val_montaj) * 1.21 : 0
        } else {
          return 0
        }
      },

      get valTransport() {
        if (Number(this.contract.transport_inclus) === 2) {
          return this.contract.val_transport ? Number(this.contract.val_transport) * 1.21 : 0
        } else {
          return 0
        }
      },

      get sumaContract() {
        let val = (fenceValueWithVat(this.contract) + otherValueWithVat(this.contract) + this.valMasuratori + this.valMontaj + this.valTransport)

        if (+this.contract.firma) {
          val = val / 1.21
        }

        return val
      },

      get sumaAvans() {
        return (this.sumaContract * this.contract.c_plata) / 100
      },

      get sumaAvans2() {
        return this.contract.c_plata2 ? (this.sumaContract * this.contract.c_plata2) / 100 : null
      },

      get sumaRest() {
        const avans2 = this.sumaAvans2 || 0
        return this.sumaContract - this.sumaAvans - avans2
      },

      get executantFirmaText() {
        return `1. CUPROLLI MANUFACTURING SRL [CUPROLLI MANUFACTURING Kft. Románia], székhelye CAPRIOAREI utca 11, GHIRODA, Timis megye, Irányítószám 307200, Románia, romániai cégjegyzékszáma J2016000468359, romániai adószáma RO 35671874, IBAN: RO89 BTRL EURC RT03 3757 8301, BIC: BTRLRO22, Bank néve: Banca Transilvania a továbbiakban iSTRUCT, képviselője Octavian Rosu, mint Ügyvezető.`
      },

      get montajFirmaText() {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. ${this.firmaMontajContract.denumire_firma.toUpperCase()} S.R.L., cu sediul in ${this.firmaMontajContract.adresa} inregistrata in Registrul Comertului sub numarul ${this.firmaMontajContract.regcom}, mit der Steuernummer ${this.firmaMontajContract.cui} (${Number(this.firmaMontajContract.vat) ? "platitor" : "neplatitor"} de TVA), cont bancar: ${this.firmaMontajContract.bank_account}, ${this.firmaMontajContract.bank}, reprezentata legal prin ${this.firmaMontajContract.person}, in calitate de MONTATOR`
      },

      get montajFirma1Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI DESIGN S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022003676350, cod fiscal 46619944 (neplatitor de TVA), cont bancar: RO74BTRLRONCRT0CH8981101, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get montajFirma2Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI PARTENER S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022005419357, cod fiscal 47219300 (neplatitor de TVA), cont bancar: RO14BTRLRONCRT0664679101, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get montajFirma3Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI PROIECTE S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022004612351, cod fiscal 46926167 (neplatitor de TVA), cont bancar: RO11BTRLRONCRT0661673901, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get actClient() {
        if (this.contract.detalii.alt_document) {
          return this.contract.detalii.alt_document + ' sorozatszáma ' + this.contract.detalii.alt_document_nr
        }
        return `C.I. serie ${this.contract.detalii.serie_ci} nr. ${this.contract.detalii.numar_ci}`
      },

      get adresaClient() {
        if (this.contract.persoana) {
          return `székhelye: ${this.contract.adresa}`
        } else {
          return `lakcíme: ${this.contract.detalii.adresa}`
        }
      },

      get contactClient() {
        const tel = String(this.contract.tel || "").trim()
        const email = String(this.contract.email || "").trim()
        return [
          tel ? `telefonszám: ${tel}` : null,
          email ? `e-mail: ${email}` : null
        ].filter(Boolean)
      },

      get moduleGard() {
        return this.contract.gard_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              // a post has no width, so it is quoted by height alone
              text: isPost(modul.tip)
                ? `${this.denumireModulGard(modul)} - M ${moduleHeightText(modul)} mm - ${modul.buc} darab`
                : `${this.denumireModulGard(modul)} - méretek H x M ${moduleSizeText(modul)} mm - ${modul.buc} darab`
            }]
          }]
        }))
      },

      get moduleCustom() {
        if (!this.contract.alte_obj) return []
        return this.contract.alte_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              text: `${this.getTranslatedName(modul.denumire)} - ${modul.cantitate} darab`
            }]
          }]
        }))
      },

      get vatText() {
        return +this.contract.firma ? "ÁFA nélkül" : "ÁFÁ-val együtt"
      },

      get price() {
        return `A termékekért, valamint adott esetben a kiegészítő szolgáltatásokért fizetendő végösszeg ${this.sumaContract.toFixed(2)} euró (${this.vatText}), amely a. ${this.contract.oferta_id} számú ${DateTime.fromISO(String(this.contract.oferta_data)).toFormat("dd.MM.yyyy")} dátumú ajánlat alapján kerül meghatározásra.`
      },

      get payment1() {
        return `Az Ügyfél kötelezettséget vállal arra, hogy az iSTRUCT részére a jelen szerződés aláírásától számított 5 munkanapon belül (vagy adott esetben a Proforma számla kiállítását követően) a megállapodott teljes vételárból ${this.sumaAvans.toFixed(2)} euró (${this.vatText}) összegű előleget fizet meg.`
      },

      get payment2() {
        return `A fennmaradó ${this.sumaRest.toFixed(2)} euró (${this.vatText}) összeg megfizetésére az Ügyfél a munkálatok befejezését és a termékek/munkák átvételi jegyzőkönyvének aláírását követően, illetve az iSTRUCT által kiállított számla alapján, legkésőbb 5 munkanapon belül köteles.`
      },

      get p1TermenExecutie() {
        if (this.contract.detalii.finisaj_finalizat) {
          return `Conform informatiei furnizare de catre beneficiar la data intocmirii acestui contract, partea de lucrare ce cade in sarcina sa, si anume, partea de zidarie, beton, stalpi de metal, etc., pe care se vor prinde reperele din aluminiu ce vor fi confectionate de executant, este complet finisata si se pot face masuratorile finale. In acest caz termenul de executie al acestor repere este de ${this.contract.t_executie} zile de la data creditarii contului beneficiarului cu suma stabilita la art VI.`
        } else {
          return `Avand in vedere ca, la data intocmirii acestui contract, beneficiarul nu are finalizata partea de lucrare ce cade in sarcina sa, si anume, partea de zidarie, beton, stalpi de metal, etc., pe care se vor prinde reperele din aluminiu ce vor fi confectionate de executant, termenul de executie a acestor repere este de ${this.contract.t_executie} zile de la data in care executantul va putea sa faca masuratorile finale.`
        }
      },

      get p2TermenExecutie() {
        if (this.contract.detalii.finisaj_finalizat) {
          return `Daca in momentul deplasarii pentru efectuarea masuratorilor, executantul constata ca beneficiarul nu are finalizata partea de lucrare ce cade in sarcina sa, se va intocmi un act aditional in care se va metiona acest lucru iar termenul de executie va fi de ${this.contract.t_executie} zile de la data in care se vor putea face masuratorile finale.`
        } else {
          return `In acest sens se va intocmi un act aditional la contract in care se va mentiona faptul ca beneficiarul are finalizata partea de lucrare ce cade in sarcina sa si executantul poate lua masuratorile finale.`
        }
      },

      get exemplareContract() {
        return this.isMontajSeparat ? "három (3)" : "két (2)"
      },

      // Helper methods
      // What a module is called in this contract, keyed by type and variant. Adding a type is a
      // key here and a line in moduleNameKey - never another branch below.
      moduleNames: {
        panel: "Panel",
        pedestrianGate: "Gyalogsági kapu",
        pedestrianGateSidePanel: "Gyalogsági kapu oldalpanellel",
        swingGateSingleLeaf: "Egyszárnyú lengőkapu",
        swingGateDoubleLeaf: "Kétszárnyú lengőkapu",
        swingGateBiFoldSingle: "Egyszárnyú bi-fold lengőkapu",
        swingGateBiFoldDouble: "Kétszárnyú bi-fold lengőkapu",
        slidingGateSingleLeaf: "Egyszárnyú tolókapu",
        slidingGateDoubleLeaf: "Kétszárnyú tolókapu",
        slidingGateTelescopic: "Teleszkópikus tolókapu",
        slidingGateArticulated: "Csuklós tolókapu",
        selfSupportingGate: "Önhordó kapu",
        posts: "Oszlopok",
        post80: "Oszlop 80 × 80 mm",
        post100: "Oszlop 100 × 100 mm",
        post200: "Oszlop 200 × 100 mm",
        framedPanel: "Kerítés keretes panel"
      },

      denumireModulGard(modul) {
        const key = moduleNameKey(modul.tip, modul.tip_poarta_mare, modul.tip_stalp)
        // a type with no key only ever comes from an offer saved with something since retired
        let name = (key && this.moduleNames[key]) || String(modul.tip || "").replace(/_/g, " ")
        if (modul.pp_included) name += " gyalogos kapuval egybeépítve"
        return name
      },

      getTranslatedName(name) {
        let newName = name
        if (name == 'Kit automatizare premium poarta culisanta - BFT Deimos Ultra') {
            newName = 'Prémium tolókapu automatizálási készlet - BFT Deimos Ultra'
        }
        if (name == 'Kit automatizare premium poarta culisanta - BFT Ares Veloce') {
            newName = 'Prémium tolókapu automatizálási készlet - BFT Ares Veloce'
        }
        if (name == 'Kit automatizare premium poarta batanta - BFT Kustos Ultra A40') {
            newName = 'Prémium kétszárnyú kapu automatizálási készlet - BFT Kustos Ultra A40'
        }
        if (name == 'Kit automatizare premium poarta batanta - BFT Virgo') {
            newName = 'Prémium kétszárnyú kapu automatizálási készlet - BFT Virgo'
        }
        return newName
      },

      formatParagraph(textContent, alignment, textType) {
        return {
          type: "paragraph",
          attrs: { textAlign: alignment },
          content: [{
            type: "text",
            text: textContent,
            ...(textType && { marks: [{ type: textType }] })
          }]
        }
      },

      // Main document structure builder
      buildDocumentStructure() {
        return {
          type: "doc",
          content: [
            // Header
            {
              type: "heading",
              attrs: { textAlign: "left", level: 1 },
              content: [{ type: "text", text: "EGYÜTTMŰKÖDÉSI SZERZŐDÉS" }]
            },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 3 },
              content: [{type: "text", text: "kerítések, kapuk és egyéb tartozékok értékesítését, illetve a szerelési munkálatok és egyéb kapcsolódó szolgáltatások biztosítását illetően"}]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Létrejött a mai map, ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")}, Ghiroda helységben.`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // I. CONTRACT PARTIES
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "I. A SZERZŐDŐ FELEK" }]
            },
            this.formatParagraph(this.executantFirmaText, "left"),
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `2. ${this.contract.nume.toUpperCase()}, ${[this.adresaClient, ...this.contactClient].join(", ")}, személyi ${this.actClient} (ha esedékes), a továbbiakban mint Megrendelő.`
              }]
            },
            { type: "blockquote"},            
            { 
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `A Felek által meghatározott záradékok:`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            // II. CONTRACT OBJECT
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "1. A SZERZŐDÉS TÁRGYA" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "1.1. A jelen Szerződés értelmében az iSTRUCT vállalat vállalja a termékek (kapuk, kerítések, alumínium tartozékok, automatizált berendezések és további alkatrészek) szállítását és telepítését/beszerelését a Megrendelő címén mely esetben az említett termékekkel kapcsolatos szolgáltatási és telepítési/beszerelési munkálatok a Megrendelő által közölt és az iSTRUCT vállalat által elfogadott előírásoknak és követelményeknek megfelelően kerülnek kivitelezésre."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "1.2. A Szerződés kivitelezésének céljából az iSTRUCT vállalat további alvállalkozókkal együttműködhet, amennyiben az adott alvállalkozók az iSTRUCT vállalat által közölt műszaki utasításoknak megfelelően biztosítják a telepítési, kezelési és szállítási munkálatok kivitelezését."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `1.3. A munka leírása`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `A teljesítés egyes elemei az alábbiak szerint kerülnek meghatározásra:`
              }]
            },
            { type: "bulletList", content: this.moduleGard },
            { type: "bulletList", content: this.moduleCustom },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Minden elem a Megrendelő által megadott ${this.contract.color} színkód szerint elektrosztatikus porszórással kerül bevonásra.`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            // III. CONTRACT DURATION
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "2. A FELEK KÖTELEZETTSÉGEI" }]
            },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 3 },
              content: [{ type: "text", text: "2.1. Az iSTRUCT vállalat kötelezettségei" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "2.1.1. A termékek szállítása: Az iSTRUCT vállalat vállalja, hogy a termékek szállítását a minőségi előírásoknak és a Megrendelővel egyeztetett előírásoknak megfelelően, illetve az ajánlatban (Melléklet/Ajánlat) részletezett módon, a közösen megállapított szállítási határidők betartásával biztosítja."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `2.1.2. A megfelelőség és garancia biztosítása:`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az iSTRUCT vállalat az alumínium termékek fényezése esetében 5 (öt) év garanciát vállal. Az említett garanciális időszak az Ügyfél általi átvétel napjával kezdődik és a kezességi tanúsítványban és/vagy az egyéb szerződéses dokumentumokban meghatározott feltételek szerint kerül biztosításra."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az iSTRUCT vállalat az automatizálási rendszerek és az elektromos tartozékok esetében 2 (két) év garanciát vállal. Az említett garanciális időszak a Megrendelő általi átvétel napjától kezdődik és a mindenkor érvényes román törvényi előírások, valamint a kezességi tanúsítványban rögzített előírások értelmében kerül biztosításra."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "A jótállási feltételeket kiegészítik a fogyasztóvédelemi és a termékek értékesítésére vonatkozó mindenkor érvényes törvényi előírások. A jótállási időszakban felmerülő hibák esetén az iSTRUCT vállalat vállalja, hogy a mindenkor érvényes törvényi előírások és a belső jótállási szabályzat értelmében a terméket megjavítja vagy kicseréli, illetve annak értékét visszatéríti."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `2.1.3. A minőségbiztosítási és kereskedelmi célokra használt fényképek`
              }]
            },
            { type: "bulletList", content: [
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "Az iSTRUCT vállalat fenntartja a jogot a munkálatok kivitelezése közbeni, illetve a munkálatok befejezését követő fényképkészítésre, mely esetben a cél a minőségbiztosítás és a műszaki dokumentálás (beleértve a telepítés megfelelőségének bizonyítását is)"
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "Az iSTRUCT vállalat külön igényelheti a Megrendelő hozzájárulását a telepített termékekről készült fényképek felhasználásához reklámanyagokban, online/offline portfóliókban vagy egyéb kereskedelmi célokra. Amennyiben a Megrendelő hozzájárulását adja, az iSTRUCT vállalat vállalja a címek vagy egyéb személyes adatok bizalmas kezelését."
                  }]
                }]
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 3 },
              content: [{ type: "text", text: "2.2. A Megrendelő kötelezettségei" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "2.2.1. A hozzáférés: A Megrendelő vállalja, hogy támogatja az iSTRUCT vállalat és/vagy az iSTRUCT felhatalmazott partnereinek (amennyiben esedékes) hozzáférését a termékek telepítésének helyszínéhez, a mérések elvégzését és a termékek leszállítását és telepítését a közösen megállapított időközben."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "2.2.2. Az átvételi ellenőrzés: A munkálatok befejezését vagy a termékek leszállítását követően a Megrendelő köteles megbizonyosodni arról, hogy a termékek és/vagy szolgáltatások megfelelnek a meghatározott követelményeknek. Ezt követően a Megrendelő aláírja az átadás-átvételi jegyzőkönyvet. Az esetleges eltéréseket a Felek írásban rögzítik és az iSTRUCT vállalat vállalja, hogy biztosítja a szerződésben rögzített előírások szerinti hibaelhárítást."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "3. A SZELLEMI TULAJDON" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "3.1. A Szerződés létrejötte előtt vagy a Szerződés teljesítésekor átadott és az iSTRUCT vállalat által szolgáltatott minden dokumentáció, útmutató, műszaki kézikönyv, műszaki információ vagy know-how az iSTRUCT vállalat kizárólagos tulajdonát képezi. Az említette anyagokat és dokumentumokat a Megrendelő kizárólag a Szerződésben vállalt kötelezettségeinek teljesítésére használhatja."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "3.2. Az iSTRUCT vállalat tulajdonát képező márkanevek, kereskedelmi megnevezések vagy egyéb hasonló jogok esetében a Megrendelőt semmilyen szellemi tulajdonjog nem illeti."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "3.3. A kerítés egy, a gyártó emblémáját tartalmazó táblával kerül ellátásra. Ezt a táblát a leszállított kapuk egyikére kell felszerelni. A felszerelés alapértelmezés szerint a gyalogoskapura történik; amennyiben ilyen nem áll rendelkezésre, a gyártó fenntartja a jogot, hogy a táblát saját belátása szerint másik kapura vagy kerítéselemre helyezze el."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "3.4. Képfelhasználási kikötés reklámcélokra"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az Ügyfél hozzájárul ahhoz, hogy az iSTRUCT a munkálatok befejezését követően fényképeket készítsen a kész projektről (pl. kerítések, kapuk, bekerítések stb.) az Ügyfél által megadott címen, feltéve, hogy a felvételek nem tartalmaznak felismerhető személyeket, gépjármű-rendszámokat, házszámokat, neveket vagy más azonosítható adatot."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az elkészített képeket az iSTRUCT reklámcélokra felhasználhatja, különösen, de nem kizárólag a vállalati honlapon, közösségi médiában, brosúrákban, prezentációkban és referencia-portfóliókban."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az Ügyfél tudomásul veszi, hogy a képek kizárólag a fent meghatározott célokra kerülnek felhasználásra, és jogosult azok eltávolítását bármikor írásbeli értesítéssel kérni."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az általános helyszín-megjelölés tekintetében a vállalat feltüntetheti a projekt általános helyét (pl. város vagy régió), azonban a pontos címet nem hozhatja nyilvánosságra"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [
                {
                type: "text",
                text: "⬜"
                },
                {
                type: "text",
                text: "  A jelölőnégyzet beikszelésével az Ügyfél kijelenti, hogy a jelen kikötéssel egyetért."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "4. A SZÁLLÍTÁSI ÉS TELEPÍTÉSI HATÁRIDŐK" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `4.1. A szállítási határidő: Az iSTRUCT vállalat vállalja, hogy a termékeket (vagy azok szállítását) a Megrendelő által közölt címre szállítja, ${this.contract.t_executie} naptári napon belül, amennyiben biztosítottak az alábbi feltételek:`
              }]
            },
            { type: "bulletList", content: [
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "A Megrendelő kifizette az előleget (az alábbi 4.1. pont szerint);"
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "Az összes szükséges műszaki részlet és mérés megerősítésre került."
                  }]
                }]
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "4.2. A telepítési határidő: A Felek vállalják a szerelési és telepítési időszakok közös meghatározását, mely esetben figyelembe veszik a munkálatok összetettségét és az iSTRUCT vagy az iSTRUCT vállalat alvállalkozóinak kapacitását. Általában a telepítési munkálatok kivitelezéséhez 30 munkanap szükséges, mely időköz az alábbi időponttól kezdődik:"
              }]
            },
            { type: "bulletList", content: [
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "Az összes műszaki részlet megerősítése és jóváhagyása;"
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "A Megrendelő által biztosított hozzáférés a helyszínhez;"
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "A termékek és/vagy bármely szükséges további anyagok biztosítása."
                  }]
                }]
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "A kivitelezéssel kapcsolatos becsült határidők és időintervallumok írásban (e-mailben, üzenetben, megrendelésben stb.) kerülnek közlésre. Amennyiben az említett időtartam a Megrendelőtől független tényezők vagy Vis Maior állapot kialakulása miatt jelentősen módosul, a Felek vállalják, hogy értesítik egymást és megbeszélik az átütemezést."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "4.3. A késések: A külső tényezők (megközelíthetetlen helyszín, kedvezőtlen időjárási körülmények, a Megrendelő által biztosított hiányos infrastruktúra, Vis Maior állapot stb.) okozta késedelmek esetén a Felek vállalják, hogy közösen meghatározzák az új szállítási/telepítési időpontokat."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "4.4. A tulajdonjog átruházása:"
              }]
            },
            { type: "orderedList", content: [
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "1. A Szerződés tárgyát képező termékek esetében a tulajdonjogok átruházása a Megrendelőre kizárólag az iSTRUCT vállalattal szemben vállalt összes fizetési kötelezettség teljesítését követően történik (ideértve, de nem kizárólagosan a termékek árát, illetve a szállítási és telepítési költségeket stb., helyzet függvényében)."
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "2. A teljes fizetésig a termékek az iSTRUCT vállalat tulajdonát képezik, akkor is, ha a szállítás már megtörtént és/vagy a termékek fizikailag a Megrendelőnél találhatók."
                  }]
                }]
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "5. AZ ÁRAK ÉS A FIZETÉSI FELTÉTELEK" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "5.1. Végösszeg"
              }]
            },
            { type: "bulletList", content: [
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: this.price
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: "A végösszeg magában foglal minden ismert és egyeztetett költséget, kivéve, ha az Ügyfél utólagos igénymódosítása miatt további költségek merülnek fel. Bármilyen költségváltoztatás kizárólag írásban érvényes, és mindkét fél aláírása szükséges hozzá."
                  }]
                }]
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "5.2. Előleg és végszámla"
              }]
            },
            { type: "bulletList", content: [
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: this.payment1
                  }]
                }]
              },
              {
                type: "listItem",
                content: [{
                  type: "paragraph",
                  attrs: { textAlign: "left" },
                  content: [{
                    type: "text",
                    text: this.payment2
                  }]
                }]
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "5.3. Fizetés: Az EUR-ban történő kifizetések az alábbi bankszámlára teljesítendők: CUPROLLI MANUFACTURING SRL, IBAN: RO89BTRLEURCRT0337578301, BIC: BTRLRO22, Bank neve: Banca Transilvania."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "5.4. Késedelmi kamat: Amennyiben az Ügyfél a fizetési határidőt elmulasztja, az iSTRUCT jogosult az Ügyfél előzetes felszólítását követően a törvényi rendelkezéseknek megfelelő késedelmi kamatot felszámítani."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "6. A FELELŐSSÉG" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "6.1. A felelősség kizárása: Az iSTRUCT vállalatot semmilyen felelősség nem terheli az alábbi esetekben:"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "a) A termékek, a készletek vagy az egyéb javak a Megrendelő hibájából, illetve a Megrendelő általi helytelen kezelés és telepítés miatt megsérülnek;"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "b) Vis Maior állapot vagy előre nem látható események miatti késedelmek vagy elmaradások;"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "c) Az infrastruktúra, az elektromos hálózatok vagy egyéb, az iSTRUCT felelősségi körébe nem tartozó komponensek megrongálódása."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "6.2 A korlátozott felelősség: Az iSTRUCT vállalat felelőssége a Megrendelő felé a Szerződéssel kapcsolatos károkra, az iSTRUCT vállalat által ténylegesen nyújtott és elismert szolgáltatások ellenértékére korlátozódik."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "6.3 A termékgarancia: Az iSTRUCT által szállított termékekre a kezességi tanúsítványban rögzített garancia és a mindenkor érvényes román törvényi előírásokban rögzített garancia (Román Fogyasztói Törvénykönyv, a 21/1992 sz. román kormányrendelet, 449/2003 sz. román törvény stb.) vonatkozik. A jótállási feltételeket a termékekhez mellékelt dokumentumok részletesen tartalmazzák."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "6.4. A hibaelhárítás: Az iSTRUCT vállalat köteles a beavatkozásokhoz szükséges elemek birtokba vételétől számított legfeljebb 30 munkanapon belül a felmerült hibák elhárítására, mely esetben az elhárítási munkálatokkal kapcsolatos költségek az iSTRUCT vállalatot terhelik; ezeket a költségeket az 1. melléklet tartalmazza."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "6.5. Szerviz és beavatkozások. "
              },
              {
                type: "text",
                text: "Amennyiben a felmerült hibákat a garancia fedezi, az iSTRUCT vállalat vállalja a javítási vagy csere költségeket, a belső szabályzatban és a mindenkor érvényes román törvény előírásokban foglaltak szerint. Amennyiben a felmerült hibákat a Megrendelő okozta (helytelen használat, szándékos rongálás, a garancia hatályán kívül eső események), a javítás költségek a Megrendelőt terhelik"
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "7. A TITOKTARTÁS" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "7.1. A Felek vállalják, hogy a Szerződés teljesítésekor megszerzett minden kereskedelmi, műszaki vagy egyéb jellegű információt bizalmasan kezelnek és azokat a másik Fél előzetes írásbeli hozzájárulása nélkül harmadik felekkel nem közlik."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "7.2. A titoktartási kötelezettség a Szerződés teljes időtartama alatt, valamint a Szerződés megszűnésétől számított 3 (három) évig érvényes, amennyiben a törvény másként nem rendelkezik, vagy ha a Felek nem állapodnak meg másként írásban"
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "8. A SZEMÉLYES ADATOK ÉS A BIZTONSÁG" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "8.1. A személyes adatok feldolgozása: A Felek vállalják, hogy betartják a Romániában és az Európai Unióban érvényes és a személyes adatok védelmére vonatkozó előírásokat (ide értve a beleértve az Általános Adatvédelmi Rendelet - GDPR előírásait is);"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Amennyiben a Szerződés teljesítése közben személyes adatok átadása történik, a Felek garantálják, hogy az adatok feldolgozása a mindenkor érvényes jogi előírások értelmében történik."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "8.2. A biztonság: A Felek vállalják, hogy meghozzák az adatbiztonsághoz és a titoktartáshoz szükséges technikai és szervezési intézkedéseket;"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "A Felek vállalják, hogy bármely biztonságsértésről vagy a személyes adatokkal kapcsolatos incidensekről haladéktalanul értesítik egymást, a mindenkor érvényes román törvényi előírásoknak megfelelően."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "9. VIS MAIOR ÉS ELŐRE NEM LÁTHATÓ ESEMÉNYEK" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "9.1. Vis Maior állapot és előre nem látható események esetén az érintett Felet a mindenkor érvényes román törvényi előírások értelmében semmilyen felelősség nem terheli, amennyiben az érintett Fél a másik Felet a mindenkor érvényes törvényi előírásokban foglalt határidőn belül értesítette."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "9.2. Amennyiben a Vis Maior állapot vagy az előre nem látható esemény időtartama meghalad 30 (harminc) egymást követő napot, bármelyik Fél jogosult a Szerződés további kártérítési igénynek nélküli felmondására."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "10. AZ IDŐTARTAM ÉS A MEGSZŰNÉS" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "10.1. AZ időtartam: A Szerződés a Felek által történő aláírással lép hatályba, és az összes kötelezettség (szállítás, fizetés, garancia) teljesítéséig jogerős marad."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "10.2. Az idő előtti megszüntetés: Bármelyik Fél jogosult a Szerződés egyoldalú felmondására, mely esetben az adott Fél köteles erről a másik Felet legalább 15 (tizenöt) nappal megelőzően írásban értesíteni. Amennyiben a Megrendelő a Szerződés egyoldalú felmondása mellett dönt, az alábbi előírások jogerőssé válnak:"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "10.2.1 Előlegfizetés esetén:"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az iSTRUCT vállalat jogosult az előleg részben vagy teljes mértékben történő visszatartására kártérítés címmel, amennyiben már megrendelte az anyagokat, a személyre szabott szolgáltatásokat (a jellegzetes színeket is) vagy egyéb, vagy kifizette a termékek gyártásával és/vagy szállításával közvetlenül kapcsolatos költségeket."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az előleg fel nem használt része (amennyiben esedékes) a már kifizetett összegeket igazoló dokumentumok alapján visszatéríthető a Megrendelőnek."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "10.2.2 Előlegfizetés hiánya:"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "A Megrendelő nem jogosult a Szerződés egyoldalú felmondására anélkül, hogy megtérítené az iSTRUCT vállalat által a megrendelés teljesítése céljából kifizetett összegeket (pl. anyagok beszerzése vagy egyedi termékek gyártása)."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Az iSTRUCT vállalat jogosult a Szerződés egyoldalú felmondásából eredő károk megtérítésére, beleértve a már megvásárolt vagy személyre szabott anyagok ellenértékét is."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Minden esetben a Felek közösen meghatározzák a már felmerült esetleges költségek megtérítésének vagy kompenzálásának módját, az iSTRUCT vállalat jogainak tiszteletben tartásával és a további kártérítések elkerülésének céljából, amennyiben az említett intézkedés a mindenkor érvényes román jogi előírások alapján indokolt."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "10.3. A teljesítéshiány miatti felmondás: Amennyiben bármelyik Fél elmulasztja kötelezettségeit és ezt a mulasztást írásbeli értesítés kézhezvételétől számított 5 (öt) napon belül nem orvosolja, a másik Fél a Szerződést további formalitások nélkül azonnali hatállyal felmondhatja."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "11. AZ ALKALMAZANDÓ JOG ÉS A JOGI HATÁSKÖR" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "11.1. A békés úton történő megoldás: A Felek vállalják, hogy mindent megtesznek annak érdekében, hogy a Szerződésből vagy azzal kapcsolatban felmerülő bármely nézeteltérést békés úton rendezzék. Amennyiben a felmerült jogviták békés úton történő rendezése a tárgyalások megkezdésétől számított 15 (tizenöt) napon nem sikerül, a Felek az illetékes bírósághoz fordulhatnak."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "11.2. Az alkalmazandó jog: A hatáskör: A Szerződést a mindenkor érvényes jogi előírások, különösen a Román Polgári Törvénykönyv és a Román Adótörvény előírásai szabályozzák. A Szerződésből vagy azzal kapcsolatban felmerülő bármely vita az iSTRUCT vállalat székhelye szerinti illetékes román bíróságok előtt kerül rendezésre, amennyiben a Felek másként nem döntenek."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "12. ZÁRÓ RENDELKEZÉSEK" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "12.1. Az engedményezés: A Megrendelő a Szerződés és a Szerződéssel kapcsolatos jogok és kötelezettségek engedményezésére kizárólag az iSTRUCT vállalat írott hozzájárulása alapján jogosult. Az iSTRUCT vállalat a jogait és kötelezettségeit harmadik személyre szabadon átruházhatja, azonban erről köteles a megrendelőt értesíteni."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "12.2. Az adatkezelési hozzájárulás: A Megrendelő kifejezetten hozzájárul ahhoz, hogy az iSTRUCT vállalat a Szerződés teljesítéséhez és felügyeléséhez szükséges adatokat (beleértve a számviteli és adózási célokat is) a személyes adatok védelmére vonatkozó jogszabályoknak megfelelően felhasználja és feldolgozza."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "12.3. Az értesítések: Bármely értesítés, kérelem, kommunikáció a Felek között írásban (beleértve az e-mailben, visszaigazolással) történik, és az átvétel vagy az elektronikus visszaigazolás napjától tekintendő érvényesnek."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "(Megjegyzés: A felek további értesítési módokat is meghatározhatnak, preferenciáik függvényében)."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "12.4. A visszalépés: A Szerződésben foglalt bármely jog gyakorlásának elmulasztása vagy késedelmes gyakorlása nem jelenti a lemondást az adott jogról. Bármely jogról történő lemondás kizárólag írásban lehetséges."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            // Contract conclusion
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `A Szerződés a mai nap _____________________ került aláírásra 2 (kettő) eredeti példányban, mindegyik Fél átvett egy-egy példányt.`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // Signatures
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "CUPROLLI MANUFACTURING SRL (iSTRUCT)"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: "Törvényes képviselője: " }, { type: "text", marks: [{ type: "bold" }], text: "Octavian Rosu" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: "Pozíció: " }, { type: "text", marks: [{ type: "bold" }], text: "Ügyvezető" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: "Aláírás és bélyegző:" }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "Megrendelő"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.nume.toUpperCase() }]
            },
            this.contract.persoana ? {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.persoana }]
            } : { type: "blockquote" },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: "Aláírás:" }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
          ]
        }
      },

      // PDF Generation
      async generatePdf(documentText) {
        const doc = await pdf_contract.createContractPDF(documentText.content, contract.country, CONTRACT_FORM_CODE)
        return doc
      }
    }
  }
}